import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import {
  Award,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
  Plus,
  Eye,
  Edit,
  Download,
  Search,
  Star,
  X,
  Save,
  Trash2,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { toast } from '../../../utils/toast';
import { useQuery } from '@tanstack/react-query';
import { useOutcomes, useCreateOutcome, useUpdateOutcome, /* useApproveOutcome, useRejectOutcome, */ useDeleteOutcome } from '../../../hooks/useOutcomes';
import { outcomeApi } from '../../../api/outcomeApi';
import { goalApi } from '../../../api/goalApi';
import { formatDate } from '../../../utils/stringUtils';
import api from '../../../api/apiClient';
import { reviewApi } from '../../../api/reviewApi';
import { getSalaryStructures } from '../../../api/salaryStructureApi';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import { formatIndianNumber } from "../../../utils/amountFormatter";
import WarningPopup from '../../../components/WarningPopup';
import useUrlTableState from '../../../hooks/useUrlTableState';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../../api/config';

const PerformanceOutcomes = ({ initialMode, id: propId, onClose, isDrawer }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    filterType: 'all',
    filterStartDate: '',
    filterEndDate: '',
    filterEmployee: 'all',
    filterEmployeeId: 'all'
  }), []);

  const {
    filters: outcomeFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateOutcomeFilter,
    updatePage: updateOutcomePage,
    updateLimit: updateOutcomeLimit,
    resetFilters: resetOutcomeFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const filterType = outcomeFilters.filterType || 'all';
  const setFilterType = useCallback((filterType) => {
    updateOutcomeFilter({ filterType });
  }, [updateOutcomeFilter]);

  const filterStartDate = outcomeFilters.filterStartDate || '';
  const filterEndDate = outcomeFilters.filterEndDate || '';

  const setDateRange = useCallback((range) => {
    updateOutcomeFilter({ filterStartDate: range.startDate || '', filterEndDate: range.endDate || '' });
  }, [updateOutcomeFilter]);

  const setFilterStartDate = useCallback((startDate) => {
    updateOutcomeFilter({ filterStartDate: startDate });
  }, [updateOutcomeFilter]);

  const setFilterEndDate = useCallback((endDate) => {
    updateOutcomeFilter({ filterEndDate: endDate });
  }, [updateOutcomeFilter]);

  const filterEmployee = outcomeFilters.filterEmployee || 'all';
  const setFilterEmployee = useCallback((filterEmployee) => {
    updateOutcomeFilter({ filterEmployee });
  }, [updateOutcomeFilter]);

  const filterEmployeeId = outcomeFilters.filterEmployeeId || 'all';
  const setFilterEmployeeId = useCallback((filterEmployeeId) => {
    updateOutcomeFilter({ filterEmployeeId });
  }, [updateOutcomeFilter]);

  const page = outcomeFilters.page;
  const limit = outcomeFilters.limit;

  const setPage = useCallback((page) => {
    updateOutcomePage(page);
  }, [updateOutcomePage]);

  const setLimit = useCallback((limit) => {
    updateOutcomeLimit(limit);
  }, [updateOutcomeLimit]);
  const [tabCounts, setTabCounts] = useState({ increments: 0, promotions: 0, incentives: 0 });
  const [activeTab, setActiveTab] = useState('increments');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [showCreateValidation, setShowCreateValidation] = useState(false);
  const [createErrors, setCreateErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [outcomeToDelete, setOutcomeToDelete] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showEditValidation, setShowEditValidation] = useState(false);
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [kpiData, setKpiData] = useState({
    totalOutcomes: 0,
    totalIncrements: 0,
    totalPromotions: 0,
    totalIncentives: 0
  });
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [loadingSalaryStructures, setLoadingSalaryStructures] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [applyingId, setApplyingId] = useState(null);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [searchTerm, filterType, filterStartDate, filterEndDate, filterEmployee, activeTab, page, limit]);

  // Role options with default permissions (matching AddEmployee.jsx)
  const roleOptions = {
    'super_admin': {
      label: 'Super Admin',
      permissions: [
        'dashboard', 'location_management', 'department_management',
        'recruitment', 'onboarding', 'employee_management', 'hr_backoffice',
        'attendance', 'payroll', 'performance', 'training', 'exit_management',
        'compliance_policy', 'reports',
        'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management',
        'analytics', 'target_achievement'
      ],
      description: 'Full system access with all CRUD operations across all modules'
    },
    'hr_manager': {
      label: 'HR Manager',
      permissions: [
        'dashboard', 'location_management', 'department_management', 'recruitment', 'onboarding',
        'employee_management', 'hr_backoffice', 'attendance', 'payroll',
        'performance', 'training', 'exit_management', 'compliance_policy', 'reports'
      ],
      description: 'Complete HRMS department management with full access to all HR functions'
    },
    'hr_executive': {
      label: 'HR Executive',
      permissions: [
        'recruitment', 'onboarding', 'employee_management',
        'attendance', 'training', 'performance'
      ],
      description: 'HR operations and support with operational-level access'
    },
    'employee': {
      label: 'Employee',
      permissions: [
        'self_service', 'my_attendance', 'my_payslip',
        'leave_request', 'my_training', 'my_performance', 'policies', 'submit_resignation'
      ],
      description: 'Employee self-service portal with access to own profile, attendance, payslips, leave, training, and performance'
    },
    'sales_manager': {
      label: 'Sales Manager',
      permissions: [
        'dashboard', 'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management',
        'analytics', 'target_achievement'
      ],
      description: 'Complete CRM and sales team management with full access to all sales functions'
    },
    'sales_executive': {
      label: 'Sales Executive',
      permissions: [
        'lead_management', 'field_sales', 'opportunity_management',
        'quotation_management', 'order_management', 'customer_management'
      ],
      description: 'Sales operations and customer engagement with standard operational access'
    },
    'dispatch_manager': {
      label: 'Dispatch Manager',
      permissions: [
        'order_management', 'customer_management', 'analytics'
      ],
      description: 'Logistics and dispatch management with order tracking and customer coordination'
    },
    'support_manager': {
      label: 'Support Manager',
      permissions: [
        'customer_management', 'order_management', 'analytics'
      ],
      description: 'Customer support operations with full access to customer relationships and order tracking'
    },
    'customer': {
      label: 'Customer',
      permissions: [
        'customer_management', 'order_management'
      ],
      description: 'Customer portal access for viewing orders and customer information'
    }
  };

  // Available roles for dropdown
  const availableRoles = [
    { value: 'super_admin', label: 'Super Admin', department: 'IT' },
    { value: 'hr_manager', label: 'HR Manager', department: 'HR' },
    { value: 'hr_executive', label: 'HR Executive', department: 'HR' },
    { value: 'employee', label: 'Employee', department: 'General' },
    { value: 'customer', label: 'Customer', department: 'Support' },
    { value: 'sales_manager', label: 'Sales Manager', department: 'Sales' },
    { value: 'sales_executive', label: 'Sales Executive', department: 'Sales' },
    { value: 'dispatch_manager', label: 'Dispatch Manager', department: 'Logistics' },
    { value: 'support_manager', label: 'Support Manager', department: 'Support' }
  ];

  const formatRoleLabel = (roleValue) => {
    if (!roleValue && roleValue !== 0) return roleValue;
    const roleString = String(roleValue).trim();
    if (!roleString) return roleString;

    const normalized = roleString.toLowerCase();
    const roleMatch = availableRoles.find(
      (role) =>
        role.value.toLowerCase() === normalized ||
        role.label.toLowerCase() === normalized
    );

    if (roleMatch) return roleMatch.label;

    return roleString
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ''))
      .join(' ')
      .trim();
  };

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle key down for number inputs (prevent negative signs and decimals)
  const handleNumberKeyDown = (e) => {
    // Prevent negative sign, decimal point, and 'e' or 'E' (scientific notation)
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };
  const [createFormData, setCreateFormData] = useState({
    employeeId: '',
    employeeName: '',
    department: '',
    outcomeType: 'increment',
    outcomeDetails: {},
    effectiveDate: '',
    justification: '',
    managerComments: '',
    hrComments: '',
    finalRating: '',
    incentiveAmount: '',
    status: 'draft'
  });

  // API Integration
  const outcomesQuery = useOutcomes({ autoFetch: false });
  const outcomesLoading = outcomesQuery.loading || outcomesQuery.isLoading;
  const createOutcomeMutation = useCreateOutcome();
  const updateOutcomeMutation = useUpdateOutcome();
  const deleteOutcomeMutation = useDeleteOutcome();

  // Fetch employees with goal statistics from backend (same as review section)
  const { data: employeesWithGoalsResponse, isLoading: employeesLoading } = useQuery({
    queryKey: ['employeesWithGoals'],
    queryFn: async () => {
      const response = await goalApi.getEmployeesWithGoals();
      // Handle different response formats
      if (response?.data?.data?.employees) {
        return response.data.data.employees;
      } else if (response?.data?.employees) {
        return response.data.employees;
      } else if (Array.isArray(response?.data)) {
        return response.data;
      }
      return [];
    },
    staleTime: 0,
    retry: 2,
  });

  // Extract employees from response
  const employees = Array.isArray(employeesWithGoalsResponse) ? employeesWithGoalsResponse : [];

  // Fetch approved reviews to determine eligible employees for outcomes
  const { data: approvedReviewsResponse, isLoading: approvedReviewsLoading } = useQuery({
    queryKey: ['approvedPerformanceReviews'],
    queryFn: async () => {
      const response = await reviewApi.getReviews({ status: 'approved' });
      if (response?.data?.data) {
        return response.data.data;
      } else if (Array.isArray(response?.data)) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }
      return [];
    },
    staleTime: 0,
  });

  // Build eligible employee list (only employees with approved reviews)
  const eligibleOutcomeEmployees = useMemo(() => {
    const reviews = Array.isArray(approvedReviewsResponse) ? approvedReviewsResponse : [];
    const uniqueMap = new Map();

    reviews.forEach((review) => {
      const key = (review.employeeEmail || review.employee || review.employeeId || '').toLowerCase();
      if (!key) return;
      if (uniqueMap.has(key)) return;

      const matchedEmployee =
        employees.find((emp) => {
          const email =
            emp.email ||
            emp.contactInfo?.personalEmail ||
            emp.user?.email;
          return email?.toLowerCase() === review.employeeEmail?.toLowerCase();
        }) || employees.find((emp) => {
          const name =
            emp.name ||
            emp.fullName ||
            `${emp.firstName || emp.personalInfo?.firstName || emp.user?.firstName || ''} ${emp.lastName || emp.personalInfo?.lastName || emp.user?.lastName || ''
              }`.trim();
          return name?.toLowerCase() === review.employee?.toLowerCase();
        });

      const value =
        matchedEmployee?.id ||
        matchedEmployee?._id ||
        matchedEmployee?.employeeId ||
        review.employeeId ||
        review.employeeEmail ||
        review.id;

      uniqueMap.set(key, {
        label: review.employee || matchedEmployee?.name || matchedEmployee?.fullName || 'Unknown',
        value,
        employeeId: matchedEmployee?.employeeId || matchedEmployee?.id || matchedEmployee?._id || review.employeeId || review.employeeEmail,
        department: matchedEmployee?.department || matchedEmployee?.employmentInfo?.department || '',
        email: review.employeeEmail,
        employeeRecord: matchedEmployee,
        review,
      });
    });

    return Array.from(uniqueMap.values());
  }, [approvedReviewsResponse, employees]);

  // Function to fetch KPI data
  const fetchKPI = useCallback(async () => {
    try {
      const response = await outcomeApi.getOutcomeKPI();
      const data = response?.data?.data || response?.data || {};
      setKpiData({
        totalOutcomes: data.totalOutcomes || 0,
        totalIncrements: data.totalIncrements || 0,
        totalPromotions: data.totalPromotions || 0,
        totalIncentives: data.totalIncentives || 0
      });
    } catch (error) {
      console.error('Error fetching KPI data:', error);
      // Keep default values on error
    }
  }, []);

  // Load salary structures
  useEffect(() => {
    const loadSalaryStructures = async () => {
      try {
        setLoadingSalaryStructures(true);
        const result = await getSalaryStructures({ status: 'active', limit: 100 });
        let structures = [];
        if (Array.isArray(result?.data?.structures)) {
          structures = result.data.structures;
        } else if (Array.isArray(result?.data)) {
          structures = result.data;
        } else if (Array.isArray(result?.structures)) {
          structures = result.structures;
        } else if (Array.isArray(result)) {
          structures = result;
        }
        const activeStructures = structures.filter(
          (structure) => (structure.status || '').toLowerCase() === 'active'
        );
        setSalaryStructures(activeStructures);
      } catch (error) {
        console.error('Error loading salary structures:', error);
        setSalaryStructures([]);
      } finally {
        setLoadingSalaryStructures(false);
      }
    };
    loadSalaryStructures();
  }, []);

  // Simple searchable, scrollable select
  const SearchSelect = ({ label, value, onChange, options, placeholder = "Search or select", required = false, error = false, helperText, showDetails = false, name, onBlur }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          if (open) {
            setOpen(false);
            if (onBlur && name) {
              // Creating a synthetic event object for handling blur
              onBlur({ target: { name, value } });
            }
          }
        }
      };

      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [open, onBlur, name, value]);

    const normalized = Array.isArray(options)
      ? options.map((opt) => {
        const mainLabel = opt.label || opt.name || opt.fullName || "";
        const employeeId = opt.employeeId || opt.id || opt._id || opt.value || "";
        const department = opt.department || "";
        return {
          label: mainLabel,
          value: opt.value || opt.id || opt._id,
          employeeId: employeeId,
          department: department,
          raw: opt
        };
      })
      : [];
    const filtered = normalized.filter((o) => {
      const searchText = query.toLowerCase();
      return o.label.toLowerCase().includes(searchText) ||
        (o.employeeId && o.employeeId.toLowerCase().includes(searchText)) ||
        (o.department && o.department.toLowerCase().includes(searchText));
    });
    return (
      <div className="relative" ref={dropdownRef}>
        {label ? (
          <label className="block text-sm font-medium text-gray-700">{label}</label>
        ) : null}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`mt-1 w-full border rounded-md p-2 flex items-center justify-between text-left outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${error ? 'border-red-500' : 'border-gray-300'}`}
        >
          <span
            className={`flex-1 block truncate ${value ? 'text-gray-900' : 'text-gray-500'}`}
            title={value || placeholder}
          >
            {value || placeholder}
          </span>
          <span className="h-4 w-4 text-gray-500">▾</span>
        </button>
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
            <div className="p-2">
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  const val = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                  setQuery(val);
                }}
                onKeyPress={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                placeholder={placeholder}
                className="w-full border border-gray-300 rounded-md p-2 text-sm outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                autoFocus
              />
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">No results</div>
              )}
              {filtered.map((opt, idx) => (
                <button
                  key={`${opt.label}-${idx}`}
                  type="button"
                  onClick={() => { onChange(opt); setOpen(false); setQuery(""); }}
                  className="w-full text-left px-3 py-2 hover:bg-blue-100/60 text-sm"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{opt.label}</span>
                    {showDetails && (opt.employeeId || opt.department) && (
                      <span className="text-xs text-gray-500 mt-0.5">
                        {opt.employeeId && opt.department
                          ? `${opt.employeeId} - ${opt.department}`
                          : opt.employeeId || opt.department}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {(helperText || (required && !value && error)) && (
          <p className={`mt-1 text-xs ${error ? 'text-red-600 flex items-center' : 'text-gray-500'}`}>
            {error && <AlertCircle className="h-4 w-4 mr-1" />}
            {helperText || 'Required'}
          </p>
        )}
      </div>
    );
  };

  // Get real data from API
  const outcomesData = outcomesQuery.outcomes || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'active': return 'bg-brand/10 text-brand';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'approved': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <AlertCircle className="h-4 w-4" />;
      case 'active': return <TrendingUp className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  // --- Validation helpers ---
  const isValidDateValue = (val) => {
    if (!val) return false;
    const dt = new Date(val);
    return !Number.isNaN(dt.getTime());
  };

  const validateOutcomeForm = (data, mode = 'create') => {
    const errors = [];
    const type = (data.outcomeType || '').toString();

    // Common required
    if (!data.employeeId) errors.push('Please select an employee');
    if (!data.department) errors.push('Department is required');
    if (!type || !['increment', 'promotion', 'incentive'].includes(type)) errors.push('Please select a valid outcome type');

    // Type specific
    if (type === 'increment') {
      // Salary structure validation is handled separately in the form
      // No need to validate newSalary here as we're using salary structures
    } else if (type === 'promotion') {
      const oldRole = (data?.outcomeDetails?.oldRole || '').toString().trim();
      const newRole = (data?.outcomeDetails?.newRole || '').toString().trim();
      if (!oldRole || !newRole) {
        errors.push('New role is required for promotion');
      } else if (oldRole.toLowerCase() === newRole.toLowerCase()) {
        errors.push('New role must be different from old role');
      }
    } else if (type === 'incentive') {
      const amt = parseFloat(data.incentiveAmount);
      if (isNaN(amt) || amt < 0 || amt > 10000000) {
        errors.push('Incentive amount must be between 0 and 10,000,000');
      }
    }

    return errors;
  };

  const validateField = (name, value, formData) => {
    let error = undefined;
    switch (name) {
      case 'employeeName': // Field name used in SearchSelect
        if (!formData.employeeId) error = "Employee is required";
        break;
      case 'outcomeType':
        if (!value) error = "Outcome type is required";
        break;
      case 'effectiveDate':
        if (!value) error = "Effective date is required";
        else {
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (selectedDate < today) error = "Effective date cannot be in the past";
        }
        break;
      case 'newSalaryStructureId':
        if (formData.outcomeType === 'increment' && !value) {
          error = "New salary structure is required";
        }
        break;
      case 'newRole':
        if (formData.outcomeType === 'promotion' && !value) {
          error = "New role is required";
        }
        break;
      case 'incentiveAmount':
        if (formData.outcomeType === 'incentive') {
          const amt = parseFloat(value);
          if (value === '' || value === undefined || value === null) {
            error = "Incentive amount is required";
          } else if (isNaN(amt)) {
            error = "Please enter a valid number";
          } else if (amt < 0) {
            error = "Amount cannot be negative";
          } else if (amt > 10000000) {
            error = "Amount cannot exceed ₹1,00,00,000";
          }
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    // For outcomeDetails fields, we need to handle them carefully
    if (name === 'newSalaryStructureId' || name === 'newRole') {
      // These are nested in outcomeDetails usually but handled differently in form
    }
    const error = validateField(name, value, createFormData);
    setCreateErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Clamp and format rating input to [0..5] with at most one decimal digit
  const sanitizeRatingInput = (val) => {
    if (val === undefined || val === null) return '';
    let s = String(val).replace(/[^0-9.]/g, '');
    // allow only one dot
    s = s.replace(/(\..*)\./g, '$1');
    // restrict integer to 1 digit, decimal to 1 digit
    if (s.includes('.')) {
      const [i, d = ''] = s.split('.');
      const i1 = i.slice(0, 1);
      const d1 = d.slice(0, 1);
      s = `${i1}${d1 ? '.' + d1 : ''}`;
    } else {
      s = s.slice(0, 1);
    }
    if (s === '') return '';
    let n = Number(s);
    if (Number.isNaN(n)) return '';
    if (n > 5) n = 5;
    if (n < 0) n = 0;
    // keep at most 1 decimal place
    return String(Math.round(n * 10) / 10);
  };

  const handleCreateOutcome = () => {
    // Default the outcome type based on active tab
    const defaultType = activeTab === 'promotions' ? 'promotion' : 'increments' ? 'increment' : 'increment';
    setCreateFormData({
      employeeId: '',
      employeeName: '',
      department: '',
      outcomeType: defaultType,
      outcomeDetails: {},
      effectiveDate: '',
      justification: '',
      managerComments: '',
      hrComments: '',
      finalRating: '',
      incentiveAmount: '',
      status: 'draft'
    });
    setCreateErrors({});
    setTouched({});
    setShowCreateModal(true);
  };

  const handleViewOutcome = (outcomeId) => {
    // Safety check for outcomesData
    if (!outcomesData || !Array.isArray(outcomesData)) {
      console.error('Outcomes data is not available:', outcomesData);
      toast.error('Outcomes data not available');
      return;
    }

    // Find the outcome in the outcomes data
    const outcome = outcomesData.find(item => (item._id || item.id) === outcomeId);

    if (outcome) {
      setSelectedOutcome(outcome);
      setShowViewModal(true);
    } else {
      toast.error('Outcome not found');
    }
  };

  const handleEditOutcome = (outcomeId) => {
    // Safety check for outcomesData
    if (!outcomesData || !Array.isArray(outcomesData)) {
      console.error('Outcomes data is not available:', outcomesData);
      toast.error('Outcomes data not available');
      return;
    }

    // Find the outcome in the outcomes data
    const outcome = outcomesData.find(item => (item._id || item.id) === outcomeId);

    if (outcome) {
      setSelectedOutcome(outcome);
      // Normalize values for edit form
      const outcomeType = outcome.outcomeType || outcome.type || '';
      const isIncrement = outcomeType === 'increment';
      const isPromotion = outcomeType === 'promotion';
      const employeeObj = outcome.employeeId && typeof outcome.employeeId === 'object' ? outcome.employeeId : outcome.employee;
      const employeeId = (outcome.employeeId && outcome.employeeId._id) || outcome.employeeId || employeeObj?._id || '';
      const employeeName = outcome.employeeName || employeeObj?.fullName || outcome.employee || '';
      const department = outcome.department || employeeObj?.employmentInfo?.department || employeeObj?.department || '';
      const effectiveDate = outcome.effectiveDate ? String(outcome.effectiveDate).slice(0, 10) : '';
      const justification = typeof outcome.justification === 'string' ? outcome.justification : (outcome.justification?.overall || '');
      const managerComments = outcome.managerComments || outcome.justification?.details || '';
      const hrComments = outcome.hrComments || outcome.justification?.hrNotes || '';

      // Extract salary structure information for increments
      const currentSalaryStructureId = outcome.outcomeDetails?.currentSalaryStructureId
        ? String(outcome.outcomeDetails.currentSalaryStructureId)
        : null;
      const newSalaryStructureId = outcome.outcomeDetails?.newSalaryStructureId
        ? String(outcome.outcomeDetails.newSalaryStructureId)
        : null;
      // Also include salary amounts as fallback if structure IDs are not available
      const currentSalary = outcome.outcomeDetails?.currentSalary ?? null;
      const newSalary = outcome.outcomeDetails?.newSalary ?? outcome.increment?.newSalary ?? outcome.increment?.amount ?? null;

      // Extract position information for promotions
      const oldPosition = outcome.outcomeDetails?.oldPosition ?? outcome.promotion?.fromRole ?? '';
      const newPosition = outcome.outcomeDetails?.newPosition ?? outcome.promotion?.toRole ?? '';
      const newRole = outcome.outcomeDetails?.newRole || '';

      const mapped = {
        employeeId,
        employeeName,
        department,
        outcomeType,
        effectiveDate,
        // Status field removed - outcomes are managed by approval workflow
        justification,
        managerComments,
        hrComments,
        finalRating: outcome.finalRating ?? '',
        outcomeDetails: isIncrement
          ? {
            currentSalaryStructureId,
            newSalaryStructureId,
            currentSalary,
            newSalary
          }
          : isPromotion
            ? {
              oldPosition,
              newPosition,
              newRole
            }
            : outcomeType === 'incentive'
              ? {
                incentiveAmount: outcome.incentive?.amount ?? outcome.outcomeDetails?.incentiveAmount ?? ''
              }
              : (outcome.outcomeDetails || {}),
      };

      setEditFormData(mapped);
      setShowEditModal(true);
    } else {
      toast.error('Outcome not found');
    }
  };

  const handleDeleteOutcome = (outcomeId) => {
    setOutcomeToDelete(outcomeId);
    setIsBulkDelete(false);
    setShowDeleteConfirm(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteConfirm(true);
  };


  const confirmDeleteOutcome = async () => {
    if (!outcomeToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteOutcomeMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
            console.error(`Error deleting outcome ${id}:`, error);
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} outcome(s) deleted successfully`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} outcome(s) could not be deleted`);
        }
      } else {
        await deleteOutcomeMutation.mutateAsync(outcomeToDelete);
        toast.success('Outcome deleted successfully!');
      }
      outcomesQuery.fetchOutcomes(buildListParams());
      refreshTabCounts();
      fetchKPI();
      setShowDeleteConfirm(false);
      setOutcomeToDelete(null);
      setIsBulkDelete(false);
    } catch (error) {
      console.error('Error deleting outcome:', error);
      if (!isBulkDelete) {
        toast.error('Failed to delete outcome. Please try again.');
      }
    }
  };

  const cancelDeleteOutcome = () => {
    setShowDeleteConfirm(false);
    setOutcomeToDelete(null);
    setIsBulkDelete(false);
  };

  const handleSendNotification = (outcomeId) => {
    toast.success(`Notification sent for outcome ${outcomeId}`);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedOutcome(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedOutcome(null);
    setEditFormData({});
  };

  const closeModals = () => {
    handleCloseCreateModal();
    handleCloseEditModal();
    handleCloseViewModal();
    if (isDrawer && onClose) {
      onClose();
    }
  };
  const handleApplyOutcome = async (outcomeId) => {
    try {
      setApplyingId(outcomeId);
      const response = await api.post(`/performance/outcomes/${outcomeId}/apply`);

      if (response?.data?.success) {
        toast.success(response.data.message || 'Outcome applied successfully');
        outcomesQuery.fetchOutcomes(buildListParams());
        refreshTabCounts();
        fetchKPI();
      } else {
        toast.error(response?.data?.message || 'Failed to apply outcome');
      }
    } catch (error) {
      console.error('Error applying outcome:', error);
      toast.error(error.response?.data?.message || 'Failed to apply outcome');
    } finally {
      setApplyingId(null);
    }
  };

  const handleSaveEdit = async () => {
    setShowEditValidation(true);

    // Validate required fields
    const errors = [];
    const nextFieldErrors = {};

    if (!editFormData.effectiveDate || editFormData.effectiveDate.trim() === '') {
      errors.push('Effective date is required');
    }

    // Validate new salary structure for increment outcomes
    if ((editFormData.outcomeType || editFormData.type) === 'increment') {
      const hasNewSalaryStructure = editFormData?.outcomeDetails?.newSalaryStructureId !== undefined &&
        editFormData?.outcomeDetails?.newSalaryStructureId !== '' &&
        editFormData?.outcomeDetails?.newSalaryStructureId !== null;
      if (!hasNewSalaryStructure) {
        nextFieldErrors.newSalaryStructureId = 'New salary structure is required';
        errors.push('New salary structure is required');
      }
    }

    // Validate new role for promotion outcomes
    if ((editFormData.outcomeType || editFormData.type) === 'promotion') {
      const hasNewRole = editFormData?.outcomeDetails?.newRole !== undefined &&
        editFormData?.outcomeDetails?.newRole !== '' &&
        editFormData?.outcomeDetails?.newRole !== null;
      const hasNewPosition = editFormData?.outcomeDetails?.newPosition !== undefined &&
        editFormData?.outcomeDetails?.newPosition !== '' &&
        editFormData?.outcomeDetails?.newPosition !== null;

      if (!hasNewRole && !hasNewPosition) {
        nextFieldErrors.newRole = 'New role is required';
        errors.push('New role is required');
      }
    }

    setEditFieldErrors(nextFieldErrors);
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    try {
      const outcomeId = selectedOutcome._id || selectedOutcome.id;
      // Send effectiveDate, justification, and newSalaryStructureId (for increments)
      const payload = {
        effectiveDate: editFormData.effectiveDate,
        justification: editFormData.justification && editFormData.justification.trim()
          ? editFormData.justification.trim()
          : ''
      };

      // Include newSalaryStructureId in outcomeDetails for increment outcomes
      if ((editFormData.outcomeType || editFormData.type) === 'increment' && editFormData.outcomeDetails?.newSalaryStructureId) {
        payload.outcomeDetails = {
          ...(payload.outcomeDetails || {}),
          newSalaryStructureId: editFormData.outcomeDetails.newSalaryStructureId
        };
      }

      // Include incentiveAmount for incentive outcomes
      if ((editFormData.outcomeType || editFormData.type) === 'incentive' && editFormData.outcomeDetails?.incentiveAmount) {
        payload.outcomeDetails = {
          ...(payload.outcomeDetails || {}),
          incentiveAmount: Number(editFormData.outcomeDetails.incentiveAmount)
        };
      }

      // Include newRole and newPosition for promotion outcomes
      if ((editFormData.outcomeType || editFormData.type) === 'promotion' && (editFormData.outcomeDetails?.newRole || editFormData.outcomeDetails?.newPosition)) {
        payload.outcomeDetails = {
          ...(payload.outcomeDetails || {}),
          newRole: editFormData.outcomeDetails.newRole || editFormData.outcomeDetails.newPosition,
          newPosition: editFormData.outcomeDetails.newPosition || editFormData.outcomeDetails.newRole
        };
      }

      await updateOutcomeMutation.mutateAsync(outcomeId, payload);

      toast.success('Outcome updated successfully!');
      closeModals();

      // Refresh the data
      outcomesQuery.fetchOutcomes(buildListParams());
      // Also refresh tab counters to keep badges in sync
      refreshTabCounts();
      // Refresh KPI data
      fetchKPI();
    } catch (error) {
      console.error('Error updating outcome:', error);
      toast.error('Failed to update outcome. Please try again.');
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateFormData({
      employeeId: '',
      employeeName: '',
      department: '',
      outcomeType: 'increment',
      outcomeDetails: {},
      effectiveDate: '',
      justification: '',
      managerComments: '',
      hrComments: '',
      finalRating: '',
      status: 'draft'
    });
  };

  const handleCreateFormChange = (field, value) => {
    setCreateFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Prefill helper: pull current role and salary structure from DB for selected employee
  const prefillFromEmployee = useCallback(async (employeeId, outcomeTypeValue, employeeRecord = null) => {
    try {
      if (!employeeId && !employeeRecord) return;
      // Find employee from the employees list if not provided
      let full = employeeRecord;
      if (!full && employeeId) {
        full = employees.find(emp => (emp.id || emp._id || emp.employeeId) === employeeId);
      }
      if (!full) return;

      // Prioritize position/designation over system role
      const currentRole = full?.employmentInfo?.position || full?.employmentInfo?.designation || full?.user?.role || full?.designation || '';
      const currentSalary = Number(full?.salaryInfo?.netSalary ?? full?.salaryInfo?.basicSalary ?? 0);

      // Get current salary structure ID
      let currentSalaryStructureId = null;
      if (full?.salaryInfo) {
        if (typeof full.salaryInfo === 'object' && full.salaryInfo._id) {
          currentSalaryStructureId = full.salaryInfo._id;
        } else if (typeof full.salaryInfo === 'string') {
          currentSalaryStructureId = full.salaryInfo;
        }
      }

      console.log('Prefilling employee data:', {
        employeeId,
        currentRole,
        currentSalary,
        currentSalaryStructureId,
        salaryInfo: full?.salaryInfo
      });

      setCreateFormData(prev => ({
        ...prev,
        department: full?.department || full?.employmentInfo?.department || prev.department || '',
        outcomeDetails: {
          ...(prev.outcomeDetails || {}),
          oldRole: currentRole || (prev.outcomeDetails?.oldRole || ''),
          currentSalary: currentSalary || (prev.outcomeDetails?.currentSalary || 0),
          currentSalaryStructureId: currentSalaryStructureId || (prev.outcomeDetails?.currentSalaryStructureId || null)
        }
      }));
    } catch (e) {
      console.error('Prefill employee info failed', e);
    }
  }, [employees]);

  const handleSaveCreate = async () => {
    setShowCreateValidation(true);
    // Debug: Log form data
    console.log('Form data:', createFormData);
    console.log('Employee ID:', createFormData.employeeId);
    console.log('Department:', createFormData.department);
    console.log('Effective Date:', createFormData.effectiveDate);
    console.log('Outcome Details:', createFormData.outcomeDetails);

    // Validation
    const errors = {};

    // Explicitly validate fields
    if (!createFormData.employeeId) {
      errors.employeeName = "Employee is required";
    }

    const dateError = validateField('effectiveDate', createFormData.effectiveDate, createFormData);
    if (dateError) errors.effectiveDate = dateError;

    if (createFormData.outcomeType === 'increment') {
      if (!createFormData.outcomeDetails?.newSalaryStructureId) {
        errors.newSalaryStructureId = "New salary structure is required";
      }
    } else if (createFormData.outcomeType === 'promotion') {
      if (!createFormData.outcomeDetails?.newRole) {
        errors.newRole = "New role is required";
      }
    } else if (createFormData.outcomeType === 'incentive') {
      const amtError = validateField('incentiveAmount', createFormData.incentiveAmount, createFormData);
      if (amtError) errors.incentiveAmount = amtError;
    }

    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);

      // Mark all relevant fields as touched
      const touchedFields = {
        employeeName: true,
        effectiveDate: true,
        outcomeType: true
      };
      if (createFormData.outcomeType === 'increment') touchedFields.newSalaryStructureId = true;
      if (createFormData.outcomeType === 'promotion') touchedFields.newRole = true;
      if (createFormData.outcomeType === 'incentive') touchedFields.incentiveAmount = true;

      setTouched(touchedFields);
      toast.error("Please fix the highlighted errors");
      return;
    }

    try {
      // Prepare API data
      const outcomeData = {
        cycleId: "current",
        employeeId: createFormData.employeeId,
        outcomeType: createFormData.outcomeType,
        outcomeDetails: createFormData.outcomeDetails,
        effectiveDate: createFormData.effectiveDate,
        // Only include justification if it has a value
        justification: createFormData.justification && createFormData.justification.trim() ? createFormData.justification.trim() : '',
        managerComments: createFormData.managerComments,
        hrComments: createFormData.hrComments,
        status: createFormData.status || 'draft'
      };

      // Map newRole to newPosition for backend compatibility (promotion)
      if (createFormData.outcomeType === 'promotion' && createFormData.outcomeDetails?.newRole) {
        outcomeData.outcomeDetails = {
          ...outcomeData.outcomeDetails,
          oldPosition: createFormData.outcomeDetails.oldRole,
          newPosition: createFormData.outcomeDetails.newRole
        };
      }

      // For increment type, ensure newSalaryStructureId is included
      if (createFormData.outcomeType === 'increment' && createFormData.outcomeDetails?.newSalaryStructureId) {
        outcomeData.outcomeDetails = {
          ...outcomeData.outcomeDetails,
          newSalaryStructureId: createFormData.outcomeDetails.newSalaryStructureId,
          currentSalaryStructureId: createFormData.outcomeDetails.currentSalaryStructureId || null
        };
      }

      // For incentive type, include the incentive amount
      if (createFormData.outcomeType === 'incentive') {
        outcomeData.incentiveAmount = parseFloat(createFormData.incentiveAmount);
      }

      // Optional final rating (0-5)
      const finalRatingNum = Number(createFormData.finalRating);
      if (!Number.isNaN(finalRatingNum) && finalRatingNum >= 0 && finalRatingNum <= 5) {
        outcomeData.finalRating = finalRatingNum;
      }

      console.log('Sending outcome data:', outcomeData);

      // Call API to create outcome
      await createOutcomeMutation.mutateAsync(outcomeData);

      // Note: Employee salary/position will be automatically updated by the scheduled job
      // when the effective date arrives. No immediate update needed.
      // Toast message is already shown in the hook, so we don't need to show it again here.

      closeModals();

      // Refresh the data
      outcomesQuery.fetchOutcomes(buildListParams());
      refreshTabCounts();
      // Refresh KPI data
      fetchKPI();
    } catch (error) {
      console.error('Error creating outcome:', error);
      // Error toast is already handled in the hook
    }
  };

  const mapOutcomesForExport = (outcomes) => {
    return outcomes.map(outcome => {
      const type = outcome.outcomeType || '';
      const oldSalary = outcome.outcomeDetails?.currentSalary ??
        outcome.employee?.salaryInfo?.netSalary ??
        outcome.employee?.salaryInfo?.basicSalary ??
        0;
      const newSalary = outcome.outcomeDetails?.newSalary ??
        outcome.increment?.newSalary ??
        outcome.increment?.amount ??
        0;

      const rawPct = outcome.outcomeDetails?.incrementPercentage ?? outcome.increment?.percentage;
      const incrementPct = rawPct ?? (oldSalary && newSalary ? ((Number(newSalary) - Number(oldSalary)) / Number(oldSalary)) * 100 : 0);
      const incrementAmt = newSalary > 0 && oldSalary > 0 ? newSalary - oldSalary : (outcome.increment?.amount || 0);

      const approvedBy = outcome.approval?.approvedBy
        ? (typeof outcome.approval.approvedBy === 'object'
          ? `${outcome.approval.approvedBy.firstName || ''} ${outcome.approval.approvedBy.lastName || ''}`.trim()
          : outcome.approval.approvedBy)
        : (outcome.appliedBy ? 'System' : 'N/A');

      const data = {
        'Employee Name': outcome.employeeName || 'N/A',
        'Employee ID': outcome.employeeId?.employeeId || outcome.employeeId?._id || outcome.employeeId || 'N/A',
        'Department': outcome.department || 'N/A',
        'Designation (Old)': outcome.outcomeDetails?.oldPosition || 'N/A',
        'Designation (New)': outcome.outcomeDetails?.newPosition || 'N/A',
        'Review Cycle': outcome.cycleName || 'N/A',
        'Performance Rating': outcome.ratingLabel || outcome.finalRating || 'N/A',
        'Increment Percentage': `${Number(incrementPct || 0).toFixed(1).replace(/\.0$/, '')}%`,
        'Increment Amount': incrementAmt,
        'Old Salary': oldSalary,
        'Revised Salary': newSalary,
        'Incentive Status': type === 'incentive' ? 'Yes' : 'No',
        'Incentive Amount': outcome.incentive?.amount ?? outcome.outcomeDetails?.incentiveAmount ?? 0,
        'Outcome Status': outcome.status || 'Pending',
        'Effective Date': outcome.effectiveDate ? formatDate(outcome.effectiveDate) : 'N/A',
        'Approved By': approvedBy,
        'Approval Date': outcome.appliedAt ? formatDate(outcome.appliedAt) : 'N/A',
        'Created Date': outcome.createdAt ? formatDate(outcome.createdAt) : 'N/A',
        'Last Updated Date': outcome.updatedAt ? formatDate(outcome.updatedAt) : 'N/A'
      };

      if (activeTab === 'increments') {
        delete data['Designation (Old)'];
        delete data['Designation (New)'];
        delete data['Promotion Status'];
        delete data['Incentive Status'];
        delete data['Incentive Amount'];
      } else if (activeTab === 'promotions') {
        delete data['Increment Percentage'];
        delete data['Increment Amount'];
        delete data['Old Salary'];
        delete data['Revised Salary'];
        delete data['Incentive Status'];
        delete data['Incentive Amount'];
      } else if (activeTab === 'incentives') {
        delete data['Designation (Old)'];
        delete data['Designation (New)'];
        delete data['Promotion Status'];
        delete data['Increment Percentage'];
        delete data['Increment Amount'];
        delete data['Old Salary'];
        delete data['Revised Salary'];
      }

      return data;
    });
  };

  const handleExportOutcomes = () => {
    if (selectedRows.size > 0) {
      const outcomesToExport = filteredOutcomes.filter(o => selectedRows.has(o._id || o.id));
      const exportData = mapOutcomesForExport(outcomesToExport);
      exportToCSV(exportData, 'performance_outcomes', {
        title: 'Performance Outcomes Export',
        type: `Selected Outcomes (${selectedRows.size})`
      });
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    const exportData = mapOutcomesForExport(filteredOutcomes);
    exportToCSV(exportData, 'performance_outcomes_page', {
      title: 'Performance Outcomes Export',
      type: 'Current Page'
    });
    setShowExportModal(false);
  };

  const handleExportRange = async (startDate, endDate) => {
    try {
      setExportLoading(true);
      const outcomeType = activeTab === 'promotions' ? 'promotion' : (activeTab === 'incentives' ? 'incentive' : 'increment');
      const params = new URLSearchParams({
        createdAtStart: startDate instanceof Date ? startDate.toISOString() : startDate,
        createdAtEnd: endDate instanceof Date ? endDate.toISOString() : endDate,
        type: 'Date Range Export',
        outcomeType: outcomeType
      });

      if (filterEmployeeId && filterEmployeeId !== 'all') params.append('employeeId', filterEmployeeId);
      if (searchTerm) params.append('q', searchTerm);

      const url = `${API_BASE_URL}/performance/outcomes/export?${params.toString()}`;
      const filename = generateExportFilename(`${activeTab}_outcomes_range`);
      await downloadFromBackend(url, filename);
      setShowExportModal(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    try {
      setExportLoading(true);
      const outcomeType = activeTab === 'promotions' ? 'promotion' : (activeTab === 'incentives' ? 'incentive' : 'increment');

      const params = new URLSearchParams({
        type: 'All Data Export',
        outcomeType: outcomeType
      });

      if (filterStartDate) params.append('createdAtStart', filterStartDate);
      if (filterEndDate) params.append('createdAtEnd', filterEndDate);
      if (filterEmployeeId && filterEmployeeId !== 'all') params.append('employeeId', filterEmployeeId);
      if (searchTerm) params.append('q', searchTerm);

      const url = `${API_BASE_URL}/performance/outcomes/export?${params.toString()}`;
      const filename = generateExportFilename(`${activeTab}_outcomes_all`);
      await downloadFromBackend(url, filename);
      setShowExportModal(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    const currentPageIds = filteredOutcomes.map(o => o._id || o.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };


  // Since we now fetch with filters server-side, just use outcomesData directly
  const filteredOutcomes = Array.isArray(outcomesData) ? outcomesData : [];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedOutcomes = useMemo(() => {
    if (!filteredOutcomes) return [];
    if (!sortField) return filteredOutcomes;

    return [...filteredOutcomes].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employee':
          aValue = (a.employeeName || a.employee || '').toLowerCase();
          bValue = (b.employeeName || b.employee || '').toLowerCase();
          break;
        case 'details':
          if (activeTab === 'increments') {
            aValue = Number(a.outcomeDetails?.newSalary || a.increment?.newSalary || a.increment?.amount || 0);
            bValue = Number(b.outcomeDetails?.newSalary || b.increment?.newSalary || b.increment?.amount || 0);
          } else {
            const rawOldA = a.outcomeDetails?.oldPosition || a.promotion?.fromRole;
            const rawNewA = a.outcomeDetails?.newPosition || a.promotion?.toRole;
            const rawOldB = b.outcomeDetails?.oldPosition || b.promotion?.fromRole;
            const rawNewB = b.outcomeDetails?.newPosition || b.promotion?.toRole;
            aValue = (rawNewA || rawOldA || '').toLowerCase();
            bValue = (rawNewB || rawOldB || '').toLowerCase();
          }
          break;
        case 'effectiveDate':
          aValue = new Date(a.effectiveDate || 0).getTime();
          bValue = new Date(b.effectiveDate || 0).getTime();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredOutcomes, sortField, sortOrder, activeTab]);

  const uniqueEmployees = [...new Set(
    (employees || []).map(emp => {
      const name = emp?.name || emp?.fullName ||
        `${emp?.firstName || emp?.personalInfo?.firstName || emp?.user?.firstName || ""} ${emp?.lastName || emp?.personalInfo?.lastName || emp?.user?.lastName || ""}`.trim();
      return name;
    }).filter(Boolean)
  )];

  const getTabStats = (tab) => {
    let outcomes = [];

    if (!outcomesData || !Array.isArray(outcomesData)) {
      return { total: 0, pending: 0, approved: 0, rejected: 0, active: 0, completed: 0 };
    }

    // Map tab keys to actual outcome types
    switch (tab) {
      case 'increments':
        outcomes = outcomesData.filter(outcome => outcome.outcomeType === 'increment');
        break;
      case 'promotions':
        outcomes = outcomesData.filter(outcome => outcome.outcomeType === 'promotion');
        break;
      case 'incentives':
        outcomes = outcomesData.filter(outcome => outcome.outcomeType === 'incentive');
        break;
      default:
        outcomes = outcomesData;
    }

    return {
      total: outcomes.length,
      pending: outcomes.filter(o => o.status === 'pending_approval' || o.status === 'pending').length,
      approved: outcomes.filter(o => o.status === 'approved').length,
      rejected: outcomes.filter(o => o.status === 'rejected').length,
      active: outcomes.filter(o => o.status === 'active').length,
      completed: outcomes.filter(o => o.status === 'completed' || o.status === 'implemented').length
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const buildListParams = useCallback(() => {
    const params = {};
    if (activeTab === 'promotions') {
      params.outcomeType = 'promotion';
    } else if (activeTab === 'incentives') {
      params.outcomeType = 'incentive';
    } else {
      params.outcomeType = 'increment';
    }
    if (filterStartDate) params.createdAtStart = filterStartDate;
    if (filterEndDate) params.createdAtEnd = filterEndDate;
    if (filterEmployeeId && filterEmployeeId !== 'all') params.employeeId = filterEmployeeId;
    if (searchTerm) params.q = searchTerm;
    params.page = page;
    params.limit = limit;
    return params;
  }, [activeTab, filterStartDate, filterEndDate, filterEmployeeId, searchTerm, page, limit]);

  // Server-side fetch on filters/search/tab change - useUrlTableState handles debouncing internally for state
  useEffect(() => {
    outcomesQuery.fetchOutcomes(buildListParams());
    fetchKPI();
  }, [buildListParams, fetchKPI]);

  // Helper to refresh tab counts (used by effect and after mutations)
  const refreshTabCounts = useCallback(async () => {
    try {
      const base = {};
      if (filterStartDate) base.createdAtStart = filterStartDate;
      if (filterEndDate) base.createdAtEnd = filterEndDate;
      if (filterEmployeeId && filterEmployeeId !== 'all') base.employeeId = filterEmployeeId;
      if (searchTerm) base.q = searchTerm;

      const [incRes, proRes, insRes] = await Promise.all([
        outcomeApi.getOutcomes({ ...base, outcomeType: 'increment', limit: 1 }),
        outcomeApi.getOutcomes({ ...base, outcomeType: 'promotion', limit: 1 }),
        outcomeApi.getOutcomes({ ...base, outcomeType: 'incentive', limit: 1 })
      ]);

      const incCount = incRes?.data?.pagination?.total || incRes?.pagination?.total || (Array.isArray(incRes?.data) ? incRes.data.length : 0);
      const proCount = proRes?.data?.pagination?.total || proRes?.pagination?.total || (Array.isArray(proRes?.data) ? proRes.data.length : 0);
      const insCount = insRes?.data?.pagination?.total || insRes?.pagination?.total || (Array.isArray(insRes?.data) ? insRes.data.length : 0);

      setTabCounts({ increments: incCount, promotions: proCount, incentives: insCount });
    } catch (e) {
      console.error('Error refreshing tab counts:', e);
    }
  }, [filterStartDate, filterEndDate, filterEmployeeId, searchTerm]);

  // Keep tab counts in sync with filters and list changes
  useEffect(() => {
    const timer = setTimeout(() => { refreshTabCounts(); }, 250);
    return () => clearTimeout(timer);
  }, [refreshTabCounts, outcomesData]);

  // Handle initial mode from props (for drawer)
  useEffect(() => {
    if (initialMode === 'create') {
      handleCreateOutcome();
    } else if (initialMode === 'view' && propId) {
      handleViewOutcome(propId);
    } else if (initialMode === 'edit' && propId) {
      handleEditOutcome(propId);
    }
  }, [initialMode, propId]);

  const renderViewContent = () => {
    if (!selectedOutcome) return null;
    const o = selectedOutcome;
    const employeeName = o.employeeName || o.employee?.fullName || o.employee || 'N/A';
    const department = o.department || o.employee?.employmentInfo?.department || 'N/A';
    const managerName = o.managerName || o.employee?.employmentInfo?.reportingManager?.fullName;
    const type = o.outcomeType || o.type || 'N/A';
    const effectiveDate = o.effectiveDate ? formatDate(o.effectiveDate, { includeTime: false }) : 'N/A';
    const currentSalary = o.outcomeDetails?.currentSalary ?? o.employee?.salaryInfo?.netSalary ?? o.employee?.salaryInfo?.basicSalary;
    const newSalary = o.outcomeDetails?.newSalary ?? o.increment?.newSalary ?? o.increment?.amount;
    const pctRaw = o.outcomeDetails?.incrementPercentage ?? o.increment?.percentage;
    const pct = pctRaw ?? (currentSalary && newSalary ? ((Number(newSalary) - Number(currentSalary)) / Number(currentSalary)) * 100 : undefined);
    const oldPosition = o.outcomeDetails?.oldPosition || o.promotion?.fromRole;
    const newPosition = o.outcomeDetails?.newPosition || o.promotion?.toRole;
    const formattedOldPosition = formatRoleLabel(oldPosition);
    const formattedNewPosition = formatRoleLabel(newPosition);
    const justificationRaw = typeof o.justification === 'string' ? o.justification : (o.justification?.overall || '');
    const justification = justificationRaw && justificationRaw.trim() ? justificationRaw.trim() : null;
    const finalRating = o.finalRating;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-500">Employee</p>
            <p className="font-medium text-gray-900 break-words">{employeeName}</p>
            {managerName ? (
              <p className="text-xs text-gray-500 break-words">Manager: {managerName}</p>
            ) : null}
          </div>
          <div>
            <p className="text-sm text-gray-500">Department</p>
            <p className="font-medium text-gray-900 break-words">{department}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Outcome Type</p>
            <p className="font-medium text-gray-900 capitalize break-words">{type}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Effective Date</p>
            <p className="font-medium text-gray-900 break-words">{effectiveDate}</p>
          </div>

          {type === 'increment' && (
            <>
              <div>
                <p className="text-sm text-gray-500">Current Salary Structure</p>
                {(() => {
                  const currentStructureId = o.outcomeDetails?.currentSalaryStructureId;
                  const currentStructure = currentStructureId
                    ? salaryStructures.find(s => {
                      const id = s.id || s._id || s.structureId;
                      return id === currentStructureId || id?.toString() === currentStructureId?.toString();
                    })
                    : null;
                  const currentSalaryDisplay = currentSalary ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(currentSalary) : null;

                  return (
                    <p className="font-medium text-gray-900 break-words">
                      {currentStructure
                        ? `${currentStructure.name || 'N/A'}${currentStructure.code ? ` · ${currentStructure.code}` : ''}${currentStructure.netPay || currentStructure.ctc ? ` · ₹${new Intl.NumberFormat('en-IN').format(currentStructure.netPay || currentStructure.ctc || 0)}` : ''}`
                        : currentSalaryDisplay
                          ? `₹${new Intl.NumberFormat('en-IN').format(currentSalary)}`
                          : 'N/A'
                      }
                    </p>
                  );
                })()}
              </div>
              <div>
                <p className="text-sm text-gray-500">New Salary Structure</p>
                {(() => {
                  const newStructureId = o.outcomeDetails?.newSalaryStructureId;
                  const newStructure = newStructureId
                    ? salaryStructures.find(s => {
                      const id = s.id || s._id || s.structureId;
                      return id === newStructureId || id?.toString() === newStructureId?.toString();
                    })
                    : null;
                  const newSalaryDisplay = newSalary ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(newSalary) : null;

                  return (
                    <p className="font-medium text-gray-900 break-words">
                      {newStructure
                        ? `${newStructure.name || 'N/A'}${newStructure.code ? ` · ${newStructure.code}` : ''}${newStructure.netPay || newStructure.ctc ? ` · ₹${new Intl.NumberFormat('en-IN').format(newStructure.netPay || newStructure.ctc || 0)}` : ''}`
                        : newSalaryDisplay
                          ? `₹${new Intl.NumberFormat('en-IN').format(newSalary)}`
                          : 'N/A'
                      }
                    </p>
                  );
                })()}
              </div>
              {pct !== undefined && !Number.isNaN(pct) && (
                <div>
                  <p className="text-sm text-gray-500">Increment</p>
                  <p className="font-medium text-gray-900 break-words">{Number(pct).toFixed(1).replace(/\.0$/, '')}%</p>
                </div>
              )}
            </>
          )}

          {type === 'promotion' && (
            <>
              <div>
                <p className="text-sm text-gray-500">Old Position</p>
                <p className="font-medium text-gray-900 break-words">{formattedOldPosition || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">New Position</p>
                <p className="font-medium text-gray-900 break-words">{formattedNewPosition || 'N/A'}</p>
              </div>
            </>
          )}

          {type === 'incentive' && (
            <div>
              <p className="text-sm text-gray-500">Incentive Amount</p>
              <p className="font-medium text-gray-900 break-words">
                {(() => {
                  const amt = o.incentive?.amount ?? o.outcomeDetails?.incentiveAmount;
                  return amt !== undefined && amt !== null
                    ? formatCurrency(amt)
                    : 'Amount not specified';
                })()}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {justification && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Justification</p>
              <p className="text-gray-900 break-words whitespace-pre-wrap">{justification}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEditForm = () => {
    if (!selectedOutcome) return null;
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee *
            </label>
            <select
              value={editFormData.employeeId || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed opacity-60"
            >
              <option value="">{editFormData.employeeName}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department
            </label>
            <input
              type="text"
              value={editFormData.department || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Outcome Type *</label>
            <select
              value={editFormData.outcomeType || editFormData.type || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed opacity-60"
            >
              <option value="increment">Salary Increment</option>
              <option value="promotion">Promotion</option>
              <option value="incentive">Incentive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Effective Date *
            </label>
            <input
              type="date"
              min={new Date().toISOString().split('T')[0]}
              value={editFormData.effectiveDate || ''}
              onChange={(e) => handleEditFormChange('effectiveDate', e.target.value)}
              onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            />
          </div>

          {(editFormData.outcomeType || editFormData.type) === 'incentive' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Incentive Amount (₹) *</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 font-medium">₹</span>
                <input
                  type="number"
                  name="incentiveAmount"
                  min="0"
                  max="10000000"
                  step="0.01"
                  value={editFormData.outcomeDetails?.incentiveAmount || ''}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val.includes('.')) {
                      const [intPart, decPart] = val.split('.');
                      if (decPart.length > 2) {
                        val = `${intPart}.${decPart.slice(0, 2)}`;
                      }
                    }
                    handleEditFormChange('outcomeDetails', { ...editFormData.outcomeDetails, incentiveAmount: val });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault();
                  }}
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                />
              </div>
            </div>
          )}

          {(editFormData.outcomeType || editFormData.type) === 'increment' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Salary Structure</label>
                {(() => {
                  const currentStructureId = editFormData.outcomeDetails?.currentSalaryStructureId;
                  const currentStructure = currentStructureId
                    ? salaryStructures.find(s => (s.id || s._id || s.structureId)?.toString() === currentStructureId?.toString())
                    : null;
                  return (
                    <input
                      type="text"
                      readOnly
                      value={currentStructure ? `${currentStructure.name} · ${currentStructure.code || ''}` : 'No structure'}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                    />
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Salary Structure *</label>
                <select
                  value={editFormData.outcomeDetails?.newSalaryStructureId || ''}
                  onChange={(e) => handleEditFormChange('outcomeDetails', { ...editFormData.outcomeDetails, newSalaryStructureId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                >
                  <option value="">Select structure</option>
                  {salaryStructures.map(s => <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>)}
                </select>
              </div>
            </>
          )}

          {(editFormData.outcomeType || editFormData.type) === 'promotion' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Role</label>
                <input
                  type="text"
                  readOnly
                  value={formatRoleLabel(editFormData.outcomeDetails?.oldRole || '')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Role *</label>
                <select
                  value={editFormData.outcomeDetails?.newRole || editFormData.outcomeDetails?.newPosition || ''}
                  onChange={(e) => handleEditFormChange('outcomeDetails', { ...editFormData.outcomeDetails, newRole: e.target.value, newPosition: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                >
                  <option value="">Select new role</option>
                  {availableRoles
                    .filter(r => {
                      if (r.value === 'super_admin') return false;
                      const rawOldRole = (editFormData.outcomeDetails?.oldRole || '').trim();
                      if (!rawOldRole) return true;
                      // Normalize to snake_case key for comparison
                      // e.g. "Employee" → "employee", "HR Manager" → "hr_manager"
                      const normalizedOldRole = rawOldRole.toLowerCase().replace(/\s+/g, '_');
                      return r.value !== normalizedOldRole && r.label.toLowerCase() !== rawOldRole.toLowerCase();
                    })
                    .map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                </select>
                {(editFormData.outcomeDetails?.newRole || editFormData.outcomeDetails?.newPosition) && roleOptions[editFormData.outcomeDetails.newRole || editFormData.outcomeDetails.newPosition] && (
                  <p className="mt-1 text-xs text-gray-500">
                    {roleOptions[editFormData.outcomeDetails.newRole || editFormData.outcomeDetails.newPosition].description}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Justification</label>
          <textarea
            value={editFormData.justification || ''}
            onChange={(e) => handleEditFormChange('justification', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
          />
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="primary" onClick={isDrawer ? onClose : handleCloseEditModal} className="bg-brand hover:bg-brand-dark text-white border-transparent">Cancel</Button>
          <Button variant="primary" onClick={handleSaveEdit} className="bg-brand hover:bg-brand-dark text-white border-transparent">Save Changes</Button>
        </div>
      </div>
    );
  };

  const renderCreateForm = () => {
    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Outcome Type <span className="text-red-500">*</span></label>
          <div className="flex items-center space-x-6">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                name="outcomeType"
                value="increment"
                checked={createFormData.outcomeType === 'increment'}
                onChange={(e) => {
                  handleCreateFormChange('outcomeType', e.target.value);
                  setCreateErrors(prev => ({ ...prev, newSalaryStructureId: undefined, newRole: undefined, incentiveAmount: undefined }));
                }}
                className="h-4 w-4 text-brand focus:ring-0 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Salary Increment</span>
            </label>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                name="outcomeType"
                value="promotion"
                checked={createFormData.outcomeType === 'promotion'}
                onChange={(e) => {
                  handleCreateFormChange('outcomeType', e.target.value);
                  setCreateErrors(prev => ({ ...prev, newSalaryStructureId: undefined, newRole: undefined, incentiveAmount: undefined }));
                }}
                className="h-4 w-4 text-brand focus:ring-0 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Promotion</span>
            </label>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                name="outcomeType"
                value="incentive"
                checked={createFormData.outcomeType === 'incentive'}
                onChange={(e) => {
                  handleCreateFormChange('outcomeType', e.target.value);
                  setCreateErrors(prev => ({ ...prev, newSalaryStructureId: undefined, newRole: undefined, incentiveAmount: undefined }));
                }}
                className="h-4 w-4 text-brand focus:ring-0 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Incentive</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SearchSelect
            label={<span>Employee <span className="text-red-500">*</span></span>}
            value={createFormData.employeeName || ''}
            name="employeeName"
            onBlur={(e) => {
              setTouched((prev) => ({ ...prev, employeeName: true }));
              const error = !createFormData.employeeId ? "Employee is required" : undefined;
              setCreateErrors((prev) => ({ ...prev, employeeName: error }));
            }}
            onChange={(opt) => {
              const empId = opt.value;
              const fullName = opt.label;
              const department = opt.department;
              handleCreateFormChange('employeeId', empId);
              handleCreateFormChange('employeeName', fullName);
              handleCreateFormChange('department', department);
              // Pass the actual record for better pre-filling
              prefillFromEmployee(empId, createFormData.outcomeType, opt.raw?.employeeRecord);

              if (createErrors.employeeName) {
                setCreateErrors(prev => ({ ...prev, employeeName: undefined }));
              }
            }}
            options={eligibleOutcomeEmployees}
            required
            error={!!(touched.employeeName && createErrors.employeeName)}
            helperText={touched.employeeName && createErrors.employeeName}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Effective Date <span className="text-red-500">*</span></label>
            <input
              type="date"
              name="effectiveDate"
              min={new Date().toISOString().split('T')[0]}
              value={createFormData.effectiveDate}
              onChange={(e) => {
                handleCreateFormChange('effectiveDate', e.target.value);
                const error = validateField('effectiveDate', e.target.value, createFormData);
                setCreateErrors(prev => ({ ...prev, effectiveDate: error }));
              }}
              onBlur={handleBlur}
              onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
              className={`w-full px-3 py-2 border rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.effectiveDate && createErrors.effectiveDate ? 'border-red-500' : 'border-gray-300'}`}
            />
            {touched.effectiveDate && createErrors.effectiveDate && (
              <p className="mt-1 text-xs text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {createErrors.effectiveDate}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
            <input
              type="text"
              readOnly
              value={createFormData.department || 'Not assigned'}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
            />
          </div>

          {/* Conditional Fields based on Outcome Type */}
          {createFormData.outcomeType === 'increment' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Salary Structure</label>
                {(() => {
                  const currentStructureId = createFormData.outcomeDetails?.currentSalaryStructureId;
                  const currentStructure = currentStructureId
                    ? salaryStructures.find(s => (s.id || s._id || s.structureId)?.toString() === currentStructureId?.toString())
                    : null;
                  return (
                    <input
                      type="text"
                      readOnly
                      value={currentStructure ? `${currentStructure.name} · ${currentStructure.code || ''}` : 'No structure'}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                    />
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Salary Structure <span className="text-red-500">*</span></label>
                <select
                  name="newSalaryStructureId"
                  value={createFormData.outcomeDetails?.newSalaryStructureId || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleCreateFormChange('outcomeDetails', { ...createFormData.outcomeDetails, newSalaryStructureId: val });
                    if (createErrors.newSalaryStructureId) {
                      setCreateErrors(prev => ({ ...prev, newSalaryStructureId: undefined }));
                    }
                  }}
                  onBlur={(e) => {
                    setTouched(prev => ({ ...prev, newSalaryStructureId: true }));
                    if (!e.target.value) {
                      setCreateErrors(prev => ({ ...prev, newSalaryStructureId: "New salary structure is required" }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.newSalaryStructureId && createErrors.newSalaryStructureId ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Select structure</option>
                  {salaryStructures.map(s => <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>)}
                </select>
                {touched.newSalaryStructureId && createErrors.newSalaryStructureId && (
                  <p className="mt-1 text-xs text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {createErrors.newSalaryStructureId}
                  </p>
                )}
              </div>
            </>
          )}

          {createFormData.outcomeType === 'promotion' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Role</label>
                <input
                  type="text"
                  readOnly
                  value={formatRoleLabel(createFormData.outcomeDetails?.oldRole || '')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Role <span className="text-red-500">*</span></label>
                <select
                  name="newRole"
                  value={createFormData.outcomeDetails?.newRole || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleCreateFormChange('outcomeDetails', { ...createFormData.outcomeDetails, newRole: val });
                    if (createErrors.newRole) {
                      setCreateErrors(prev => ({ ...prev, newRole: undefined }));
                    }
                  }}
                  onBlur={(e) => {
                    setTouched(prev => ({ ...prev, newRole: true }));
                    if (!e.target.value) {
                      setCreateErrors(prev => ({ ...prev, newRole: "New role is required" }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg bg-white outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.newRole && createErrors.newRole ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Select new role</option>
                  {availableRoles
                    .filter(r => {
                      if (r.value === 'super_admin') return false;
                      const rawOldRole = (createFormData.outcomeDetails?.oldRole || '').trim();
                      if (!rawOldRole) return true;
                      // Normalize to snake_case key for comparison
                      // e.g. "Employee" → "employee", "HR Manager" → "hr_manager"
                      const normalizedOldRole = rawOldRole.toLowerCase().replace(/\s+/g, '_');
                      return r.value !== normalizedOldRole && r.label.toLowerCase() !== rawOldRole.toLowerCase();
                    })
                    .map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                </select>
                {touched.newRole && createErrors.newRole && (
                  <p className="mt-1 text-xs text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {createErrors.newRole}
                  </p>
                )}
                {createFormData.outcomeDetails?.newRole && roleOptions[createFormData.outcomeDetails.newRole] && (
                  <p className="mt-1 text-xs text-gray-500">
                    {roleOptions[createFormData.outcomeDetails.newRole].description}
                  </p>
                )}
              </div>
            </>
          )}

          {createFormData.outcomeType === 'incentive' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Incentive Amount (₹) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 font-medium">₹</span>
                <input
                  type="number"
                  name="incentiveAmount"
                  min="0"
                  max="10000000"
                  step="0.01"
                  value={createFormData.incentiveAmount}
                  onChange={(e) => {
                    let val = e.target.value;
                    // Allow only up to 2 decimal places
                    if (val.includes('.')) {
                      const [intPart, decPart] = val.split('.');
                      if (decPart.length > 2) {
                        val = `${intPart}.${decPart.slice(0, 2)}`;
                      }
                    }
                    handleCreateFormChange('incentiveAmount', val);
                    // Clear error on change
                    const err = validateField('incentiveAmount', val, { ...createFormData, outcomeType: 'incentive' });
                    setCreateErrors(prev => ({ ...prev, incentiveAmount: err }));
                  }}
                  onBlur={(e) => {
                    setTouched(prev => ({ ...prev, incentiveAmount: true }));
                    const err = validateField('incentiveAmount', e.target.value, { ...createFormData, outcomeType: 'incentive' });
                    setCreateErrors(prev => ({ ...prev, incentiveAmount: err }));
                  }}
                  onKeyDown={(e) => {
                    // Prevent negative sign and 'e'/'E'
                    if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault();
                  }}
                  placeholder="e.g. 25000.00"
                  className={`w-full pl-7 pr-3 py-2 border rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.incentiveAmount && createErrors.incentiveAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
              </div>
              <p className="mt-1 text-xs text-gray-400">Maximum: ₹1,00,00,000 · Up to 2 decimal places</p>
              {touched.incentiveAmount && createErrors.incentiveAmount && (
                <p className="mt-1 text-xs text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {createErrors.incentiveAmount}
                </p>
              )}
            </div>
          )}

        </div>{/* end grid */}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Justification</label>
          <textarea
            value={createFormData.justification}
            onChange={(e) => handleCreateFormChange('justification', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
          />
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="primary" onClick={isDrawer ? onClose : handleCloseCreateModal} className="bg-brand hover:bg-brand-dark text-white border-transparent">Cancel</Button>
          <Button variant="primary" onClick={handleSaveCreate} className="bg-brand hover:bg-brand-dark text-white border-transparent">Create Outcome</Button>
        </div>
      </div>
    );
  };

  if (isDrawer) {
    return (
      <div className="p-6">
        {initialMode === 'create' && renderCreateForm()}
        {initialMode === 'edit' && renderEditForm()}
        {initialMode === 'view' && renderViewContent()}
        {(initialMode === 'view' || initialMode === 'edit') && !selectedOutcome && (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance Outcomes</h1>
          <p className="text-gray-600">Manage increments, promotions and incentives</p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedRows.size > 0 && (
            <PermissionGate permission="performance" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 border-red-600"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="performance" action="read">
            <Button
              variant="outline"
              icon={<Download className="h-4 w-4" />}
              onClick={handleExportOutcomes}
            >
              Export {selectedRows.size > 0 ? `(${selectedRows.size})` : ''}
            </Button>
          </PermissionGate>
          <PermissionGate permission="performance" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleCreateOutcome}
              className="bg-brand hover:bg-brand-dark text-white border-transparent hover:text-white"
            >
              Create Outcome
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Outcome Stats */}
      {(() => {
        const KpiCard = ({ icon: Icon, title, value, subtitle, color, trend }) => {
            let bgColor = "bg-[#0F2942]";
            if (color?.includes("brand") || color?.includes("blue")) bgColor = "bg-[#0F2942]";
            else if (color?.includes("red")) bgColor = "bg-[#DC2626]";
            else if (color?.includes("green") || color?.includes("emerald")) bgColor = "bg-[#16A34A]";
            else if (color?.includes("purple") || color?.includes("indigo")) bgColor = "bg-[#7C3AED]";
            else if (color?.includes("yellow") || color?.includes("orange") || color?.includes("amber")) bgColor = "bg-[#D97706]";

            return (
                <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 group">
                    <div className="space-y-4 flex-1">
                        <h3 className="text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase">
                            {title}
                        </h3>
                        <div className="flex items-baseline space-x-2">
                            <span className="text-[28px] font-bold text-slate-900 leading-none tracking-tight">
                                {value}
                            </span>
                            {(subtitle || trend) && (
                                <div className="flex items-center space-x-2">
                                    {subtitle && (
                                        <span className="text-sm font-medium text-slate-500">
                                            {subtitle}
                                        </span>
                                    )}
                                    {trend && (
                                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${trend.color} shadow-sm`}>
                                            {trend.label}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center text-white shadow-sm transform transition-transform duration-300 group-hover:scale-110 flex-shrink-0 ml-4`}>
                        <Icon className="w-5 h-5" />
                    </div>
                </div>
            );
        };

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <KpiCard
              icon={Award}
              title="Total Outcomes"
              value={formatIndianNumber(kpiData.totalOutcomes)}
              color="bg-brand"
            />
            <KpiCard
              icon={TrendingUp}
              title="Increments"
              value={formatIndianNumber(kpiData.totalIncrements)}
              color="bg-green-600"
            />
            <KpiCard
              icon={Award}
              title="Promotions"
              value={formatIndianNumber(kpiData.totalPromotions)}
              color="bg-purple-600"
            />
            <KpiCard
              icon={Star}
              title="Incentives"
              value={formatIndianNumber(kpiData.totalIncentives)}
              color="bg-yellow-500"
            />
          </div>
        );
      })()}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { key: 'increments', label: 'Increments', count: tabCounts.increments },
              { key: 'promotions', label: 'Promotions', count: tabCounts.promotions },
              { key: 'incentives', label: 'Incentives', count: tabCounts.incentives }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
                  ? 'border-brand text-brand'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab.label}
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${activeTab === tab.key
                  ? 'bg-brand text-white'
                  : 'bg-gray-100 text-gray-600'
                  }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap items-end gap-4 w-full">
              <div className="relative flex-[2] min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Employee..."
                  value={searchTerm}
                  onChange={(e) => {
                    // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                    const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                    setSearchTerm(value);
                  }}
                  onKeyPress={(e) => {
                    // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                    if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  maxLength={100}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      const input = document.querySelector('input[placeholder="Search outcomes..."]');
                      if (input) input.focus();
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex-shrink-0">
                <DateRangeFilter
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  onChange={setDateRange}
                />
              </div>
              {(searchTerm || filterStartDate || filterEndDate || filterEmployeeId !== 'all') && (
                <div className="relative flex-shrink-0">
                  <Button
                    variant="outline"
                    icon={<X className="h-4 w-4" />}
                    onClick={() => {
                      resetOutcomeFilters();
                    }}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Outcomes List */}
          {outcomesLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
              <span className="ml-2 text-brand font-medium">Loading outcomes...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 capitalize">
                <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded"
                        onChange={handleSelectAll}
                        checked={filteredOutcomes.length > 0 && selectedRows.size === filteredOutcomes.length}
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort('employee')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Employee</span>
                        {sortField === 'employee' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort('details')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{activeTab === 'increments' ? 'Salary Details' : 'Role Change'}</span>
                        {sortField === 'details' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>

                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort('effectiveDate')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Effective Date</span>
                        {sortField === 'effectiveDate' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created At</span>
                        {sortField === 'createdAt' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOutcomes.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <FileText className="h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No outcomes found</h3>
                          {(searchTerm || filterStartDate || filterEndDate || filterEmployeeId !== 'all') && (
                            <p className="text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
                          )}
                          {!searchTerm && !filterStartDate && !filterEndDate && filterEmployeeId === 'all' && (
                            <p className="text-sm text-gray-500">Get started by creating your first outcome.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sortedOutcomes.map((outcome) => (
                      <tr
                        key={outcome._id || outcome.id}
                        className={`hover:bg-blue-50/30 ${(selectedRows.has(outcome._id) || selectedRows.has(outcome.id)) ? 'bg-brand/5' : ''}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded"
                            checked={selectedRows.has(outcome._id || outcome.id)}
                            onChange={() => handleSelectRow(outcome._id || outcome.id)}
                          />
                        </td>
                        <td className="px-6 py-4 max-w-xs">
                          <div>
                            <div className="text-sm font-medium text-gray-900 truncate" title={outcome.employeeName || outcome.employee || 'N/A'}>
                              {outcome.employeeName || outcome.employee || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500 truncate" title={outcome.department || 'N/A'}>{outcome.department || 'N/A'}</div>
                            {(() => {
                              const managerDisplay = outcome.managerName || outcome.manager;
                              return managerDisplay && managerDisplay !== 'N/A' ? (
                                <div className="text-xs text-gray-400 truncate" title={`Manager: ${managerDisplay}`}>Manager: {managerDisplay}</div>
                              ) : null;
                            })()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {activeTab === 'increments' && (
                            <div>
                              {(() => {
                                const rawPct = (outcome?.outcomeDetails?.incrementPercentage ?? outcome?.increment?.percentage);
                                const newSal = (outcome?.outcomeDetails?.newSalary ?? outcome?.increment?.newSalary ?? outcome?.increment?.amount);
                                const curSal = (outcome?.outcomeDetails?.currentSalary);
                                const hasBoth = Number(curSal) > 0 && Number(newSal) > 0;
                                const derivedPct = hasBoth ? ((Number(newSal) - Number(curSal)) / Number(curSal)) * 100 : undefined;
                                const pct = (rawPct !== undefined && rawPct !== null && !Number.isNaN(rawPct)) ? Number(rawPct) : (derivedPct !== undefined ? derivedPct : undefined);
                                return (
                                  <>
                                    <div className="text-sm text-gray-900">
                                      {hasBoth
                                        ? `${formatCurrency(curSal)} → ${formatCurrency(newSal)}`
                                        : (newSal ? `New salary: ${formatCurrency(newSal)}` : 'Increment details not available')}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {pct !== undefined && pct !== null && !Number.isNaN(pct) && Number(pct) > 0
                                        ? `${Number(pct).toFixed(1).replace(/\.0$/, '')}% increment`
                                        : ''}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                          {activeTab === 'promotions' && (
                            <div>
                              <div className="text-sm text-gray-900">
                                {(() => {
                                  const rawOldRole = outcome.outcomeDetails?.oldPosition || outcome.promotion?.fromRole;
                                  const rawNewRole = outcome.outcomeDetails?.newPosition || outcome.promotion?.toRole;
                                  const formattedOldRole = formatRoleLabel(rawOldRole);
                                  const formattedNewRole = formatRoleLabel(rawNewRole);

                                  if (formattedOldRole && formattedNewRole) {
                                    return <span>{`${formattedOldRole} → ${formattedNewRole}`}</span>;
                                  }

                                  if (formattedNewRole) {
                                    return <span>{formattedNewRole}</span>;
                                  }

                                  return <span>New position not specified</span>;
                                })()}
                              </div>
                            </div>
                          )}
                          {activeTab === 'incentives' && (
                            <div>
                              <div className="text-sm text-gray-900">
                                {(() => {
                                  const amt = outcome.incentive?.amount ?? outcome.outcomeDetails?.incentiveAmount;
                                  return amt !== undefined && amt !== null
                                    ? formatCurrency(amt)
                                    : 'Amount not specified';
                                })()}
                              </div>
                              <div className="text-xs text-gray-400">One-time payment</div>
                            </div>
                          )}
                          {/* PIP row removed */}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {outcome.effectiveDate ?
                            formatDate(outcome.effectiveDate, { includeTime: false }) :
                            'N/A'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {outcome.createdAt ?
                            formatDate(outcome.createdAt, { includeTime: true }) :
                            'N/A'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <PermissionGate permission="performance" action="read">
                              <button
                                onClick={() => handleViewOutcome(outcome._id || outcome.id)}
                                className="p-2 text-brand hover:text-brand-dark hover:bg-brand/10 rounded-lg transition-colors"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            {!outcome.isApplied && !(outcome.effectiveDate && new Date(outcome.effectiveDate) < new Date().setHours(0, 0, 0, 0)) && (
                              <>
                                <PermissionGate permission="performance" action="update">
                                  <button
                                    onClick={() => handleEditOutcome(outcome._id || outcome.id)}
                                    className="p-2 text-brand hover:text-brand-dark hover:bg-brand/10 rounded-lg transition-colors"
                                    title="Edit"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                                <PermissionGate permission="performance" action="delete">
                                  <button
                                    onClick={() => handleDeleteOutcome(outcome._id || outcome.id)}
                                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                                <PermissionGate permission="performance" action="update">
                                  <button
                                    onClick={() => handleApplyOutcome(outcome._id || outcome.id)}
                                    disabled={applyingId === (outcome._id || outcome.id)}
                                    className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Apply Outcome"
                                  >
                                    {applyingId === (outcome._id || outcome.id) ? (
                                      <div className="h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                      <CheckCircle className="h-4 w-4" />
                                    )}
                                  </button>
                                </PermissionGate>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {!outcomesLoading && outcomesData.length > 0 && (
            <PaginationControls
              currentPage={outcomesQuery.pagination?.page || 1}
              totalPages={outcomesQuery.pagination?.totalPages || 0}
              totalItems={outcomesQuery.pagination?.total || 0}
              limit={outcomesQuery.pagination?.limit || 10}
              hasNextPage={(outcomesQuery.pagination?.page || 1) < (outcomesQuery.pagination?.totalPages || 0)}
              hasPrevPage={(outcomesQuery.pagination?.page || 1) > 1}
              onPageChange={(newPage) => setPage(newPage)}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
              }}
              border={false}
            />
          )}
        </div>
      </div>

      {/* View Outcome Modal */}
      {showViewModal && selectedOutcome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 bg-brand rounded-t-lg">
              <h3 className="text-lg font-semibold text-white">View Outcome Details</h3>
              <button
                onClick={handleCloseViewModal}
                className="text-white hover:bg-white/10 p-1 rounded-full transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 capitalize">
              {renderViewContent()}
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <Button
                variant="primary"
                onClick={handleCloseViewModal}
                className="bg-brand hover:bg-brand-dark text-white border-transparent px-6"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Outcome Modal */}
      {showEditModal && selectedOutcome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 bg-brand rounded-t-lg">
              <h3 className="text-lg font-semibold text-white">Edit Outcome</h3>
              <button
                onClick={handleCloseEditModal}
                className="text-white hover:bg-white/10 p-1 rounded-full transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {renderEditForm()}
            </div>
          </div>
        </div>
      )}

      {/* Create Outcome Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 bg-brand rounded-t-lg">
              <h3 className="text-lg font-semibold text-white">Create New Outcome</h3>
              <button
                onClick={handleCloseCreateModal}
                className="text-white hover:bg-white/10 p-1 rounded-full transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {renderCreateForm()}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <WarningPopup
        isOpen={showDeleteConfirm}
        onClose={cancelDeleteOutcome}
        onConfirm={confirmDeleteOutcome}
        title={isBulkDelete ? "Delete Outcomes" : "Delete Outcome"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected outcome(s)? This action cannot be undone.`
            : "Are you sure you want to delete this outcome? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportRange={handleExportRange}
        onExportAll={handleExportAll}
        loading={exportLoading}
        title="Export Performance Outcomes"
      />
    </div>
  );
};

export default PerformanceOutcomes;